import React, { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import Popover from "components/common/Popover";
import { keySeparator } from "views/Constants";
import OnClickPopoverValueGroups from "views/components/visualizations/OnClickPopover/OnClickPopoverValueGroups";
import getHoverSwatchColor from "views/components/visualizations/utils/getHoverSwatchColor";
import useUserDateTime from "hooks/useUserDateTime";
import { adjustFormat, toUTCFromTz, isValidDate } from "util/DateTime";
import PopoverTitle from "views/components/visualizations/OnClickPopover/PopoverTitle";
const DivContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings.xxs};
  `
);
const CartesianOnClickPopoverDropdown = ({
  clickPoint,
  config,
  setFieldData,
  setStep,
  showBackButton = false
}) => {
  const { t } = useTranslation("aggregation");
  const traceColor = getHoverSwatchColor(clickPoint);
  const { userTimezone } = useUserDateTime();
  const convertToOriginalValue = useCallback(
    (value) => {
      if (config.isTimeline && typeof value === "string" && isValidDate(value))
        return adjustFormat(toUTCFromTz(value, userTimezone), "internalIndexer");
      return value;
    },
    [config.isTimeline, userTimezone]
  );
  const { rowPivotValues, columnPivotValues, metricValue } = useMemo(() => {
    var _a, _b, _c, _d, _e, _f;
    if (!clickPoint || !config) return {};
    const splitNames = ((_a = clickPoint.data.originalName) != null ? _a : clickPoint.data.name).split(
      keySeparator
    );
    const metric = config.series.length === 1 ? config.series[0].function : splitNames.pop();
    const columnValues = splitNames.filter((value) => value !== metric);
    const columnPivotsToFields = (_c = (_b = config == null ? void 0 : config.columnPivots) == null ? void 0 : _b.flatMap((pivot) => pivot.fields)) != null ? _c : [];
    const rowPivotsToFields = (_e = (_d = config == null ? void 0 : config.rowPivots) == null ? void 0 : _d.flatMap((pivot) => pivot.fields)) != null ? _e : [];
    const splitXValues = (rowPivotsToFields == null ? void 0 : rowPivotsToFields.length) ? `${String(clickPoint.x)}`.split(keySeparator) : [];
    return {
      rowPivotValues: splitXValues.map((labelValue, i) => {
        const value = convertToOriginalValue(labelValue);
        return {
          value,
          field: rowPivotsToFields[i],
          text: String(labelValue),
          traceColor
        };
      }),
      columnPivotValues: columnValues.map((value, i) => ({
        value,
        field: columnPivotsToFields[i],
        text: String(value),
        traceColor
      })),
      metricValue: {
        value: clickPoint.y,
        field: metric,
        text: `${String((_f = clickPoint.text) != null ? _f : clickPoint.y)}`,
        traceColor
      }
    };
  }, [clickPoint, config, convertToOriginalValue, traceColor]);
  const onBackToTraces = useCallback(() => setStep("traces"), [setStep]);
  return /* @__PURE__ */ React.createElement(
    Popover.Dropdown,
    {
      title: /* @__PURE__ */ React.createElement(PopoverTitle, { onBackClick: showBackButton && onBackToTraces }, t("vis_popover.related_values"))
    },
    /* @__PURE__ */ React.createElement(DivContainer, null, /* @__PURE__ */ React.createElement(
      OnClickPopoverValueGroups,
      {
        columnPivotValues,
        metricValue,
        rowPivotValues,
        setFieldData,
        config
      }
    ))
  );
};
export default CartesianOnClickPopoverDropdown;
